import { useCallback } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Form, FormField, SubmitButton, TextInput } from 'components/Forms';
import { Heading } from 'components/Typography';
import { ContactInfoContext, withContactInfoMachineState } from 'domains/signup/contactInfo/data/ContactInfoContext';
import { isPhoneNumberValid } from 'utils/phoneNumber';
import { TFunction } from 'i18next';

type EnterPhoneForm = {
  phoneNumber: string;
};

const getFormSchema = (t: TFunction): yup.ObjectSchema<EnterPhoneForm> =>
  yup
    .object({
      phoneNumber: yup
        .string()
        .ensure()
        .test('is-valid-phone-number', t('form.validation.phoneNumberInvalid'), isPhoneNumberValid)
        .required(t('form.validation.fieldIsRequired')),
    })
    .required();

type Props = {
  defaultValues?: {
    phoneNumber?: string;
  };
  onSubmit: SubmitHandler<EnterPhoneForm>;
};

const EnterPhone: React.FC<Props> = ({ defaultValues, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <Form
      onSubmit={onSubmit}
      schema={getFormSchema(t)}
      defaultValues={defaultValues}
      validationMode="onSubmit"
      flashErrors={false}
    >
      <Heading level="h2" className="mb-16">
        {t(`signup.steps.enterPhone.title`)}
      </Heading>
      <Heading level="h7" className="mb-64 sm-d:mb-40">
        {t(`signup.steps.enterPhone.subtitle`)}
      </Heading>
      <FormField
        data-testid="phone-number-input"
        component={TextInput}
        name="phoneNumber"
        disabled={false}
        label={t(`signup.steps.enterPhone.label`)}
        placeholder="(123) 456-7890"
        className="mb-64 sm-d:mb-40"
        hint={t('signup.steps.enterPhone.hint')}
      />
      <SubmitButton data-testid="phone-number-submit-button" className="sm-d:w-full" size="lg" willRedirect={false}>
        {t('signup.steps.enterPhone.submit')}
      </SubmitButton>
    </Form>
  );
};
const ContactInfoWrapper = () => {
  const phoneNumber = ContactInfoContext.useSelector(({ context }) => context.phoneNumber);
  const { send } = ContactInfoContext.useActorRef();

  const onSubmit: SubmitHandler<EnterPhoneForm> = useCallback(
    ({ phoneNumber }) => {
      send({
        type: 'submitPhoneNumber',
        phoneNumber,
      });
    },
    [send],
  );

  return (
    <EnterPhone
      defaultValues={{
        phoneNumber: phoneNumber,
      }}
      onSubmit={onSubmit}
    />
  );
};

export const ContactInfoEnterPhone = withContactInfoMachineState(ContactInfoWrapper, 'enterPhoneNumber');
